<template>
  <SfModal
    v-if="modalOpen"
    size="l"
    class="sf-border-box"
    data-walkthrough-id="account-entries-modal"
    inner-modal-classes="sf-overflow-hidden"
    :footer-padding="0"
    :scrollable-content="true"
    @close="close"
  >
    <template #title>{{
      external
        ? I18n.t('company_links.select_external_accounts')
        : I18n.t('account_selector_modal.select_account')
    }}</template>

    <template #body>
      <div
        v-if="isLoading"
        class="tw-p-6 tw-flex tw-items-center tw-justify-center"
      >
        <div class="sf-spinner"></div>
      </div>
      <div v-else>
        <SelectedExternalCompany
          v-if="isCompanySelected"
          :ledger-label="ledgerLabel"
          :selected-company="selectedCompany"
        />
        <div class="tw-grid tw-grid-cols-2 tw-gap-6">
          <div class="tw-flex tw-flex-col">
            <div class="sf-uppercase tw-mb-2">
              {{ I18n.t('account_selector_modal.selected_accounts') }}
            </div>
            <div
              class="
                tw-grow
                tw-border
                tw-border-solid
                tw-border-sf-snow-dark
                tw-rounded-lg
                tw-overflow-y-auto
                tw-h-[530px]
              "
              data-testid="selected-accounts-tab"
            >
              <div
                v-for="account in selectedAccounts"
                :key="account.id"
                class="
                  tw-group
                  tw-flex
                  tw-justify-between
                  tw-items-center
                  tw-p-4
                  tw-relative
                  tw-border-0
                  tw-border-b
                  tw-border-solid
                  tw-border-sf-snow-dark
                  tw-text-sm
                  tw-text-sf-gray
                  hover:tw-bg-sf-snow-light hover:tw-text-sf-gray-darker
                "
                data-test="selected-account"
              >
                <div
                  class="fa fa-check tw-text-sf-success-light-2 tw-mr-2"
                ></div>
                <div class="tw-flex tw-grow tw-items-center tw-justify-between">
                  <div>
                    <div
                      v-if="showOriginalName(account)"
                      class="sf-c-text-light sf-below-1"
                    >
                      {{ account.originalNumberWithOriginalName }}
                    </div>
                    <span class="sf-fwdb sf-c-text-dark">{{
                      account.numberWithName
                    }}</span>
                  </div>
                  <SfButton
                    variant=""
                    :small="true"
                    class="tw-opacity-0 group-hover:tw-opacity-100"
                    @click="deselectAccount(account)"
                    >{{ I18n.t('common.remove') }}</SfButton
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="tw-flex tw-flex-col">
            <div class="sf-uppercase tw-mb-2">
              {{ I18n.t('account_selector_modal.available_accounts') }}
            </div>
            <div
              class="
                tw-flex tw-px-4 tw-py-2 tw-mb-4 tw-rounded-lg tw-bg-sf-snow
              "
            >
              <input
                v-model="searchText"
                class="sf-form-input-transparent sf-full !tw-pl-0"
                data-test="search-text"
                placeholder="Filter"
                type="text"
              />
              <SfButton variant="" :small="true" @click="selectFiltered">{{
                I18n.t('account_selector_modal.select_all')
              }}</SfButton>
            </div>
            <div
              class="
                tw-grow
                tw-border
                tw-border-solid
                tw-border-sf-snow-dark
                tw-rounded-lg
                tw-overflow-y-auto
                tw-h-[470px]
              "
              data-testid="available-accounts-tab"
            >
              <div
                v-for="account in filteredAccounts"
                :key="account.id"
                class="
                  tw-flex
                  tw-justify-between
                  tw-items-center
                  tw-p-4
                  tw-relative
                  tw-border-0
                  tw-border-b
                  tw-border-solid
                  tw-border-sf-snow-dark
                  tw-text-sm
                  tw-text-sf-gray
                  hover:tw-bg-sf-snow-light hover:tw-text-sf-gray-darker
                "
                data-test="available-account"
              >
                <div class="tw-flex tw-grow tw-items-center tw-justify-between">
                  <div>
                    <div
                      v-if="showOriginalName(account)"
                      class="sf-c-text-light sf-below-1"
                    >
                      {{ account.originalNumberWithOriginalName }}
                    </div>
                    <span class="sf-fwdb sf-c-text">{{
                      account.numberWithName
                    }}</span>
                  </div>
                  <SfButton
                    variant=""
                    :small="true"
                    @click="selectAccount(account)"
                    >{{ I18n.t('common.select') }}
                  </SfButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div
        class="
          tw-px-6
          tw-py-4
          tw-w-full
          tw-border-solid
          tw-border
          tw-border-b-0
          tw-border-l-0
          tw-border-r-0
          tw-border-sf-neutral-lighter-1
        "
      >
        <div class="sf-level sf-level-between">
          <div class="sf-level">
            <SfButton v-if="canDeselect" variant="" @click="deselectAll"
              ><span>{{
                I18n.t('account_selector_modal.clear_all')
              }}</span></SfButton
            >
            <SfBannerInline
              v-if="isDefaultChanged"
              variant="warning"
              icon-class="fa-info-circle"
              :title="I18n.t('account_selector_modal.changed_from_default')"
            >
              <template #actions>
                <SfButton
                  variant=""
                  :small="true"
                  :class="{ 'tw-text-sf-warning': true }"
                  @click="restoreDefault"
                  >{{
                    I18n.t('account_selector_modal.restore_default')
                  }}</SfButton
                >
              </template>
            </SfBannerInline>
          </div>
          <div class="sf-level">
            <SfButton variant="" @click="close">{{
              I18n.t('common.form.cancel')
            }}</SfButton>
            <SfButton variant="success" @click="save">{{
              I18n.t('common.save')
            }}</SfButton>
          </div>
        </div>
      </div>
    </template>
  </SfModal>
</template>

<script>
import api from '../account-entries-modal/api/index'
import { doesAccountNameSatisfyQuery } from '@app/utils/account-search-helpers.ts'
import { SfModal } from '@app/shared/vue/components/sf-modal'
import _ from 'lodash'
import { SfLog } from '@app/adapters/logger'
import SfButton from '@app/shared/vue/components/sf-button/sf-button.vue'
import SelectedExternalCompany from '@app/application/company_links/components/selected-external-company'
import SfBannerInline from '@app/shared/vue/components/sf-banner/sf-banner-inline.vue'

export default {
  components: {
    SfModal,
    SfButton,
    SelectedExternalCompany,
    SfBannerInline,
  },

  data() {
    return {
      modalOpen: true,
      isLoading: false,
      selectedAccounts: [],
      otherAccounts: [],
      defaultAccounts: [],
      updatePath: null,
      afterUpdate: null,
      updateParams: {},
      searchText: '',
      range: null,
      defaultValue: null,
      value: null,
      external: false,
      ledgerLabel: null,
    }
  },

  computed: {
    valueToSubmit() {
      if (this.defaultAccounts.length && !this.isDefaultChanged) {
        return ''
      } else if (this.selectedAccounts.length) {
        return this.selectedAccounts.map((value) => `#${value.id}`).join(',')
      } else {
        return '~!'
      }
    },

    filteredAccounts() {
      const accountsToShow = this.searchText
        ? this.otherAccounts.filter((account) =>
            this.isAccountSatisfyQuery(account, this.searchText)
          )
        : this.otherAccounts

      return _.sortBy(accountsToShow, (acc) => acc.numberWithName)
    },

    isDefaultChanged() {
      const hasDifferentItems =
        this.selectedAccounts?.length !== this.defaultAccounts?.length ||
        _.differenceWith(
          this.selectedAccounts,
          this.defaultAccounts,
          this.compareAccount
        ).length !== 0

      return this.defaultAccounts?.length && hasDifferentItems
    },

    isCompanySelected() {
      return this.external && this.selectedCompany
    },

    canDeselect() {
      return this.selectedAccounts?.length > 0
    },
  },

  methods: {
    openModal({
      defaultValue,
      value,
      attemptToConfirmUnreview,
      range,
      updatePath,
      afterUpdate,
      objectLedgerId,
      external,
      ledgerLabel,
      dataToSubmit,
      externalCompanyId,
      ...updateParams
    }) {
      this.attemptToConfirmUnreview = attemptToConfirmUnreview
      this.updatePath = updatePath
      this.updateParams = updateParams
      this.afterUpdate = afterUpdate
      this.objectLedgerId = objectLedgerId
      this.range = range
      this.defaultValue = defaultValue
      this.value = value
      this.modalOpen = true
      this.isLoading = true
      this.external = external
      this.ledgerLabel = ledgerLabel
      this.dataToSubmit = dataToSubmit

      if (external) {
        this.fetchExternalSelectorAccounts(externalCompanyId)
      } else {
        this.fetchSelectorAccounts()
      }
    },

    async fetchSelectorAccounts() {
      this.isLoading = true

      try {
        const response = await api.getSelectorModalAccounts({
          range: this.range,
          defaultValue: this.defaultValue,
          value: this.value,
          objectLedgerId: this.objectLedgerId,
        })

        const { accounts, selectedAccounts, defaultAccounts } = response || {}
        this.selectedAccounts = selectedAccounts
        this.otherAccounts = _.differenceWith(
          accounts,
          this.selectedAccounts,
          this.compareAccount
        )
        this.defaultAccounts = defaultAccounts
        this.isLoading = false
      } catch (e) {
        SfLog.warn('Error while fetching accounts for the selector', {
          extra: {
            error: e,
          },
        })
      }
    },

    async fetchExternalSelectorAccounts(newlySelectedCompanyId = null) {
      this.isLoading = true

      try {
        const response = await api.getExternalSelectorModalAccounts({
          range: this.range,
          defaultValue: this.defaultValue,
          value: this.value,
          objectLedgerId: this.objectLedgerId,
          selectedCompanyId: newlySelectedCompanyId,
        })

        const { accounts, selectedAccounts, defaultAccounts, selectedCompany } =
          response || {}

        this.selectedAccounts = selectedAccounts
        this.otherAccounts = _.differenceWith(
          accounts,
          this.selectedAccounts,
          this.compareAccount
        )
        this.defaultAccounts = defaultAccounts
        this.selectedCompany = selectedCompany
        this.isLoading = false
      } catch (e) {
        SfLog.warn('Error while fetching external accounts for the selector', {
          extra: {
            error: e,
          },
        })
      }
    },

    close() {
      this.modalOpen = false
      this.searchText = ''
    },

    save() {
      // attemptToConfirmUnreview is used to skip the confirmation when saving
      // data on the proposal edit page
      if (this.attemptToConfirmUnreview) {
        this.saveWithUnreviewConfirmation()
      } else {
        return this.performSave()
      }
    },

    saveWithUnreviewConfirmation() {
      window.eventHub.$emit('confirmUnreviewTemplate', {
        callback: () => {
          this.performSave()
        },
      })
    },

    performSave() {
      const properties = {
        properties: [
          {
            ...this.updateParams,
            objectLedgerId: this.objectLedgerId,
            value: this.valueToSubmit,
          },
          ...this.dataToSubmit,
        ],
      }

      this.isLoading = true
      api
        .updateTextProperties({ updatePath: this.updatePath, properties })
        .then((response) => {
          Silverfin.Spinner.success()
          this.close()
          this.afterUpdate(response)
          window.eventHub.$emit('reloadWorkflowReviewStatusData')
        })
        .catch(() => {
          Silverfin.Spinner.error()
        })
        .then(() => {
          this.isLoading = false
        })
    },
    selectFiltered() {
      this.filteredAccounts.forEach(this.selectAccount)
      this.searchText = ''
    },

    deselectAll() {
      // Since we're mutating selectedAccounts we need to create new array out of it first
      ;[...this.selectedAccounts].forEach(this.deselectAccount)
    },

    restoreDefault() {
      const accountsToDeselect = _.differenceWith(
        this.selectedAccounts,
        this.defaultAccounts,
        this.compareAccount
      )
      const accountsToSelect = _.differenceWith(
        this.defaultAccounts,
        this.selectedAccounts,
        this.compareAccount
      )

      accountsToDeselect.forEach(this.deselectAccount)
      accountsToSelect.forEach(this.selectAccount)
    },

    selectAccount(account) {
      const accountIndex = this.otherAccounts.findIndex((a) =>
        this.compareAccount(a, account)
      )
      if (accountIndex === -1) {
        return
      }

      this.otherAccounts.splice(accountIndex, 1)
      this.selectedAccounts.push(account)
    },

    deselectAccount(account) {
      const accountIndex = this.selectedAccounts.findIndex((a) =>
        this.compareAccount(a, account)
      )
      if (accountIndex === -1) {
        return
      }

      this.selectedAccounts.splice(accountIndex, 1)
      this.otherAccounts.push(account)
    },

    showOriginalName(account) {
      return account.originalNumberWithOriginalName !== account.numberWithName
    },

    compareAccount(account, otherAccount) {
      return account.id === otherAccount.id
    },

    isAccountSatisfyQuery(account, query) {
      return (
        doesAccountNameSatisfyQuery(account.numberWithName, query) ||
        doesAccountNameSatisfyQuery(
          account.originalNumberWithOriginalName,
          query
        )
      )
    },
  },
}
</script>
